import React, { createContext, useContext, useCallback, useMemo } from 'react'
import { useAuth } from './AuthContext'
import type { UserRole } from '../types'

type Feature = 'moderation' | 'branding' | 'user-management' | 'audit'

interface RoleContextType {
  role: UserRole | null
  hasRole: (role: UserRole) => boolean
  canAccess: (feature: Feature) => boolean
}

const RoleContext = createContext<RoleContextType | null>(null)

// Role hierarchy: system-admin > institution-admin > uni-admin > researcher
const ROLE_HIERARCHY: Record<UserRole, number> = {
  'system-admin': 4,
  'institution-admin': 3,
  'uni-admin': 2,
  'researcher': 1,
}

// Feature access requirements (minimum role level needed)
const FEATURE_ACCESS: Record<Feature, UserRole> = {
  'moderation': 'uni-admin',
  'branding': 'uni-admin',
  'user-management': 'institution-admin',
  'audit': 'institution-admin',
}

export function RoleProvider({ children }: { children: React.ReactNode }) {
  const { user } = useAuth()
  const role = user?.role || null

  const hasRole = useCallback(
    (requiredRole: UserRole): boolean => {
      if (!role) return false
      return ROLE_HIERARCHY[role] >= ROLE_HIERARCHY[requiredRole]
    },
    [role]
  )

  const canAccess = useCallback(
    (feature: Feature): boolean => {
      if (!role) return false
      const requiredRole = FEATURE_ACCESS[feature]
      if (!requiredRole) return false
      return ROLE_HIERARCHY[role] >= ROLE_HIERARCHY[requiredRole]
    },
    [role]
  )

  const value = useMemo<RoleContextType>(
    () => ({ role, hasRole, canAccess }),
    [role, hasRole, canAccess]
  )

  return <RoleContext.Provider value={value}>{children}</RoleContext.Provider>
}

export function useRole(): RoleContextType {
  const context = useContext(RoleContext)
  if (!context) {
    throw new Error('useRole must be used within a RoleProvider')
  }
  return context
}
